<template>
  <div>
  <div class="member-perki">
    <h1 class="page-title">Member PERKI</h1>

    <div class="search-bar">
      <a-input-search
        v-model:value="searchQuery"
        placeholder="Cari nama anggota"
        allow-clear
        @search="handleSearch"
        @change="e => !e.target.value && handleSearch()"
      />
    </div>

    <div class="table-wrapper">
      <a-table
        :columns="columns"
        :data-source="members"
        :loading="loading"
        :pagination="false"
        row-key="id"
        :scroll="{ x: 780 }"
        @change="handleTableChange"
      >
        <template #pengurus="{ record }">
          {{ record.pengurus || '-' }}
        </template>

        <template #pengurusFilter="{ confirm, clearFilters }">
          <div style="padding: 8px; display: flex; flex-direction: column; gap: 8px; min-width: 200px;">
            <a-input
              v-model:value="pengurusFilter"
              placeholder="Cari pengurus..."
              allow-clear
              @pressEnter="() => { confirm(); handleSearch() }"
            />
            <div style="display: flex; gap: 8px;">
              <a-button
                type="primary"
                size="small"
                style="flex:1; background:#e10f28; border-color:#e10f28;"
                @click="() => { confirm(); handleSearch() }"
              >Cari</a-button>
              <a-button
                size="small"
                style="flex:1;"
                @click="() => { pengurusFilter = ''; clearFilters(); handleSearch() }"
              >Reset</a-button>
            </div>
          </div>
        </template>

        <template #pengurusFilterIcon>
          <search-outlined :style="{ color: pengurusFilter ? '#e10f28' : undefined }" />
        </template>

        <template #status="{ record }">
          <div class="status-cell">
            <a-switch
              :checked="record.isActive"
              :loading="togglingId === record.id"
              class="status-switch"
              :class="{ 'switch-active': record.isActive }"
              @change="checked => handleToggle(record, checked)"
            />
            <span class="status-tag">
              {{ record.isActive ? 'Aktif' : 'Nonaktif' }}
            </span>
          </div>
        </template>

        <template #actions="{ record }">
          <button class="btn-ubah" @click="openEdit(record)">
            <edit-outlined class="btn-icon" />
            UBAH
          </button>
        </template>
      </a-table>
    </div>

    <div class="pagination-bar">
      <span class="pagination-info">
        Menampilkan {{ members.length }} dari {{ pagination.total }} baris
      </span>
      <div class="pagination-controls">
        <a-pagination
          v-model:current="pagination.current"
          :total="pagination.total"
          :page-size="pagination.pageSize"
          :show-size-changer="true"
          :page-size-options="['10', '20', '50']"
          @change="handlePageChange"
          @showSizeChange="handleSizeChange"
        />
      </div>
    </div>
  </div>

  <!-- Edit Modal -->
  <member-perki-edit-modal
    :visible="editModalVisible"
    :member-id="editMemberId"
    @close="editModalVisible = false"
    @saved="onSaved"
  />

  <!-- Toggle Confirm Modal -->
  <a-modal
    v-model:visible="showToggleModal"
    :footer="null"
    :closable="false"
    :centered="true"
    width="400px"
    :body-style="{ padding: 0 }"
  >
    <div class="toggle-modal">
      <div class="toggle-modal-title">
        {{ pendingToggleChecked ? 'Aktifkan' : 'Nonaktifkan' }} {{ pendingToggleRecord && pendingToggleRecord.name }}?
      </div>
      <div class="toggle-modal-desc">
        <template v-if="pendingToggleChecked">
          Member yang diaktifkan kembali dapat mengakses kartu member dan fitur eksklusif lainnya.
        </template>
        <template v-else>
          Member yang dinonaktifkan tidak dapat mengakses kartu member dan fitur eksklusif lainnya. Member tetap dapat mengakses akun sebagai role publik.
        </template>
      </div>
      <div class="toggle-modal-footer">
        <button class="toggle-btn-cancel" @click="showToggleModal = false">Batal</button>
        <button class="toggle-btn-confirm" :disabled="togglingId !== null" @click="confirmToggle">
          {{ togglingId !== null ? 'Memproses...' : 'Ya, Lanjutkan' }}
        </button>
      </div>
    </div>
  </a-modal>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { notification } from 'ant-design-vue'
import { EditOutlined, SearchOutlined } from '@ant-design/icons-vue'
import { mobileCmsMemberAPI } from '@/services/axios/perki/mobileCms'
import MemberPerkiEditModal from './MemberPerkiEditModal.vue'

const columns = [
  {
    title: 'Nama',
    dataIndex: 'name',
    key: 'name',
    sorter: true,
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
    width: 240,
  },
  {
    title: 'NPA',
    dataIndex: 'perkiId',
    key: 'perkiId',
    width: 120,
    sorter: true,
  },
  {
    title: 'Pengurus',
    key: 'pengurus',
    width: 160,
    slots: { customRender: 'pengurus', filterDropdown: 'pengurusFilter', filterIcon: 'pengurusFilterIcon' },
  },
  {
    title: 'Status',
    key: 'status',
    width: 160,
    slots: { customRender: 'status' },
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 120,
    slots: { customRender: 'actions' },
  },
]

export default {
  name: 'MemberPerki',
  components: { EditOutlined, SearchOutlined, MemberPerkiEditModal },
  setup() {
    const members = ref([])
    const loading = ref(false)
    const togglingId = ref(null)
    const searchQuery = ref('')
    const pengurusFilter = ref('')
    const pagination = ref({ current: 1, pageSize: 10, total: 0 })

    const editModalVisible = ref(false)
    const editMemberId = ref(null)

    const showToggleModal = ref(false)
    const pendingToggleRecord = ref(null)
    const pendingToggleChecked = ref(false)

    const sortField = ref(null)
    const sortOrder = ref(null)

    const loadMembers = async () => {
      loading.value = true
      try {
        const params = {
          name: searchQuery.value,
          page: pagination.value.current,
          pageSize: pagination.value.pageSize,
        }
        if (pengurusFilter.value) params.pengurus = pengurusFilter.value
        if (sortField.value) {
          params.sortBy = sortField.value
          params.sortOrder = sortOrder.value === 'descend' ? 'desc' : 'asc'
        }
        const res = await mobileCmsMemberAPI.getLinked(params)
        const result = res.data?.result
        members.value = result?.data || []
        pagination.value.total = Number(result?.totalData) || 0
      } catch {
        notification.error({ message: 'Error', description: 'Gagal memuat data member.' })
      } finally {
        loading.value = false
      }
    }

    const handleSearch = () => {
      pagination.value.current = 1
      loadMembers()
    }

    const handlePageChange = page => {
      pagination.value.current = page
      loadMembers()
    }

    const handleSizeChange = (_, size) => {
      pagination.value.pageSize = size
      pagination.value.current = 1
      loadMembers()
    }

    const handleTableChange = (_, __, sorter) => {
      sortField.value = sorter?.field || null
      sortOrder.value = sorter?.order || null
      pagination.value.current = 1
      loadMembers()
    }

    const handleToggle = (record, checked) => {
      pendingToggleRecord.value = record
      pendingToggleChecked.value = checked
      showToggleModal.value = true
    }

    const confirmToggle = async () => {
      const record = pendingToggleRecord.value
      const checked = pendingToggleChecked.value
      const original = record.isActive
      record.isActive = checked
      togglingId.value = record.id
      showToggleModal.value = false
      try {
        await mobileCmsMemberAPI.toggleActive(record.id)
        notification.success({
          message: 'Berhasil',
          description: checked ? 'Member berhasil diaktifkan kembali.' : 'Member berhasil dinonaktifkan.',
          placement: 'topRight',
          class: 'premium-notification',
          duration: 3,
        })
      } catch {
        record.isActive = original
        notification.error({ message: 'Error', description: 'Gagal mengubah status member.' })
      } finally {
        togglingId.value = null
      }
    }

    const openEdit = record => {
      editMemberId.value = record.id
      editModalVisible.value = true
    }

    const onSaved = () => {
      editModalVisible.value = false
      loadMembers()
    }

    onMounted(loadMembers)

    return {
      members, loading, togglingId, searchQuery, pengurusFilter, pagination, columns,
      editModalVisible, editMemberId,
      showToggleModal, pendingToggleRecord, pendingToggleChecked,
      handleSearch, handlePageChange, handleSizeChange, handleTableChange,
      handleToggle, confirmToggle, openEdit, onSaved,
    }
  },
}
</script>

<style scoped>
.member-perki {
  padding: 0;
  background: #fcfcfc;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.page-title {
  font-family: 'Lato', sans-serif;
  font-size: 30px;
  font-weight: 700;
  color: #282828;
  line-height: 40px;
  margin: 0;
}

/* Search bar */
.search-bar :deep(.ant-input-affix-wrapper) {
  border: 1px solid #e4e9f0;
  border-radius: 8px;
  padding: 8px 12px;
  background: #fff;
  box-shadow: none;
  height: 38px;
}

.search-bar :deep(.ant-input-affix-wrapper:focus),
.search-bar :deep(.ant-input-affix-wrapper-focused) {
  border-color: #e10f28;
  box-shadow: 0 0 0 2px rgba(225, 15, 40, 0.1);
}

.search-bar :deep(.ant-input) {
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: #444;
}

.search-bar :deep(.ant-input::placeholder) {
  color: #798892;
}

/* Table wrapper */
.table-wrapper {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0px 4px 10px 0px rgba(20, 19, 34, 0.03), 0px 0px 10px 0px rgba(20, 19, 34, 0.02);
}

:deep(.ant-table-thead > tr > th) {
  background: #f2f4f8 !important;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #444;
  border-bottom: 1px solid #eee !important;
  height: 48px;
  padding: 10px 8px;
}

:deep(.ant-table-thead > tr > th::before) {
  display: none;
}

:deep(.ant-table-tbody > tr > td) {
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: #444;
  border-bottom: 1px solid #eee !important;
  height: 56px;
  padding: 8px;
}

:deep(.ant-table-tbody > tr:hover > td) {
  background: #fafafa !important;
}

:deep(.ant-table) {
  background: #fff;
}

/* Status cell */
.status-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

:deep(.status-switch.ant-switch) {
  background-color: #d1d5db;
}

:deep(.status-switch.switch-active.ant-switch) {
  background-color: #dc2626;
}

.status-tag {
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: #183e6f;
  background: #fafafa;
  border: 0.5px solid #d9d9d9;
  border-radius: 50px;
  padding: 0 8px;
  line-height: 22px;
  white-space: nowrap;
}

/* UBAH button */
.btn-ubah {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 2px solid #282828;
  border-radius: 50px;
  background: #fff;
  color: #282828;
  font-family: 'Barlow', 'Lato', sans-serif;
  font-weight: 700;
  font-size: 14px;
  padding: 6px 12px;
  cursor: pointer;
  line-height: 22px;
  transition: border-color 0.2s, color 0.2s;
}

.btn-ubah:hover {
  border-color: #e10f28;
  color: #e10f28;
}

.btn-icon {
  font-size: 14px;
}

/* Pagination */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pagination-info {
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: #444;
}

:deep(.ant-pagination-item-active) {
  border-color: #e10f28;
}

:deep(.ant-pagination-item-active a) {
  color: #e10f28;
}

:deep(.ant-pagination-item:hover) {
  border-color: #e10f28;
}

:deep(.ant-pagination-item:hover a) {
  color: #e10f28;
}

:deep(.ant-pagination-prev:hover .ant-pagination-item-link),
:deep(.ant-pagination-next:hover .ant-pagination-item-link) {
  border-color: #e10f28;
  color: #e10f28;
}

/* Toggle confirm modal */
.toggle-modal {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.toggle-modal-title {
  font-size: 20px;
  font-weight: 700;
  color: #062d4b;
  line-height: 28px;
}

.toggle-modal-desc {
  font-size: 14px;
  color: #444;
  line-height: 22px;
}

.toggle-modal-footer {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.toggle-btn-cancel {
  padding: 8px 20px;
  border-radius: 50px;
  border: 1.5px solid #282828;
  background: #fff;
  color: #282828;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  min-height: 40px;
}

.toggle-btn-confirm {
  padding: 8px 20px;
  border-radius: 50px;
  border: 1.5px solid #e10f28;
  background: #e10f28;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  min-height: 40px;
}

.toggle-btn-confirm:disabled {
  background: #98a2b3;
  border-color: #98a2b3;
  cursor: not-allowed;
}

/* Notification */
:deep(.premium-notification) {
  border-radius: 12px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

:deep(.premium-notification .ant-notification-notice-message) {
  font-weight: 700;
  margin-bottom: 4px;
}

:deep(.premium-notification .ant-notification-notice-description) {
  font-size: 14px;
  color: #4b5563;
}
</style>
